import React from 'react';
import { Box, Button, Typography, Paper, Alert, AlertTitle } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { monitoringService } from '../services/monitoring.service';

/**
 * Error Boundary Component
 * Catches React component errors and logs them to monitoring system
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  async componentDidCatch(error, errorInfo) {
    // Log error to monitoring system
    try {
      const errorData = {
        message: error.toString(),
        stack: error.stack || '',
        componentStack: errorInfo.componentStack || '',
        url: window.location.href,
        userAgent: navigator.userAgent,
        severity: 'high',
        metadata: {
          componentName: this.props.componentName || 'Unknown Component',
          timestamp: new Date().toISOString()
        }
      };

      await monitoringService.logFrontendError(errorData);

      // Store error info in state for display
      this.setState({
        error,
        errorInfo,
        errorId: Date.now().toString(36) // Simple error ID
      });

      // Also log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
      }
    } catch (loggingError) {
      console.error('Failed to log error to monitoring system:', loggingError);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 3
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 600,
              width: '100%',
              p: 4,
              textAlign: 'center'
            }}
          >
            <ErrorOutline
              sx={{
                fontSize: 80,
                color: 'error.main',
                mb: 2
              }}
            />

            <Typography variant="h4" gutterBottom color="error">
              Oops! Something went wrong
            </Typography>

            <Typography variant="body1" color="textSecondary" paragraph>
              We're sorry, but something unexpected happened. The error has been logged and we'll look into it.
            </Typography>

            {this.state.errorId && (
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <AlertTitle>Error Reference</AlertTitle>
                Error ID: <strong>{this.state.errorId}</strong>
                <br />
                Please reference this ID when contacting support.
              </Alert>
            )}

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="error" sx={{ textAlign: 'left' }}>
                  <AlertTitle>Error Details (Development Mode)</AlertTitle>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {this.state.error.toString()}
                  </Typography>
                </Alert>

                {this.state.errorInfo?.componentStack && (
                  <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                    <AlertTitle>Component Stack</AlertTitle>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.75rem',
                        maxHeight: 200,
                        overflow: 'auto'
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReset}
                startIcon={<Refresh />}
              >
                Try Again
              </Button>

              <Button
                variant="outlined"
                color="primary"
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
            </Box>

            {this.props.showContactSupport !== false && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
                If the problem persists, please contact support with the error ID above.
              </Typography>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Functional wrapper for ErrorBoundary with hooks support
 */
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  return (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

/**
 * Mini error boundary for smaller sections
 */
export const MiniErrorBoundary = ({ children, componentName }) => {
  return (
    <ErrorBoundary
      componentName={componentName}
      fallback={
        <Alert severity="error" sx={{ m: 2 }}>
          <AlertTitle>Component Error</AlertTitle>
          This section encountered an error. Please try refreshing the page.
        </Alert>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
